/**
 * Platform abstraction layer.
 *
 * Every web/native divergence in the CircleUp app flows through this folder.
 * Components should import `@/platform/<feature>` and never branch on
 * `window.` or `document.` directly. That way a single React codebase runs
 * unchanged inside both the Vite-served Rails SPA and the Capacitor Android
 * (and later iOS) shell.
 */

export type PlatformName = 'web' | 'android' | 'ios'

/**
 * True when the bundle is running inside a Capacitor native shell.
 *
 * Uses a defensive `any` lookup so this module stays importable in jsdom
 * tests and in the web build (where `@capacitor/core` is not installed yet).
 * Once Capacitor is added as a dependency we will replace this with a direct
 * `import { Capacitor } from '@capacitor/core'`.
 */
export function isNative(): boolean {
  if (typeof window === 'undefined') return false
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
  return Boolean(cap?.isNativePlatform?.())
}

export function platformName(): PlatformName {
  if (typeof window === 'undefined') return 'web'
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor
  const name = cap?.getPlatform?.()
  if (name === 'android') return 'android'
  if (name === 'ios') return 'ios'
  return 'web'
}

/**
 * Runtime selection helper that mirrors React Native's `Platform.select`.
 *
 * Example:
 *   const baseUrl = selectPlatform({
 *     web: '',
 *     native: 'https://api.circleup.ch',
 *   })
 */
export function selectPlatform<T>(options: { web?: T; native?: T; android?: T; ios?: T }): T | undefined {
  const name = platformName()
  if (name === 'android' && options.android !== undefined) return options.android
  if (name === 'ios' && options.ios !== undefined) return options.ios
  if (name !== 'web' && options.native !== undefined) return options.native
  return options.web
}
